import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output, QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {AnalysisMethodsService} from '../../../services/config/analysis-methods.service';
import {ProcessRolesService} from '../../../services/config/process-roles.service';
import {UserService} from '../../../services/user.service';
import {OrganizationService} from '../../../services/organization.service';
import {DecisionService} from '../../../services/decision.service';
import {ActivatedRoute, Router} from '@angular/router';
import {SurveyService} from '../../../services/surveys.service';
import {MatTable, MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {FormControl} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {ModalSurveyDetailsComponent} from "../../../modals/modal-survey-details/modal-survey-details.component";
import {animate, state, style, transition, trigger} from "@angular/animations";

@Component({
    selector: 'app-all-surveys',
    templateUrl: './all-surveys.component.html',
    styleUrls: ['./all-surveys.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ maxHeight: '0', overflow: 'hidden' })),
            state('expanded', style({ maxHeight: '2000px', overflow: 'hidden' })),
            transition('collapsed <=> expanded', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class AllSurveysComponent implements OnInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @ViewChildren('innerSort') innerSort: QueryList<MatSort>;
  @ViewChildren('innerTables') innerTables: QueryList<MatTable<Survey>>;

  @Input() projectId: any;
  @Input() input_surveys: any;
  @Input() addable: boolean = true;
  @Input() surveyViewable: boolean = true;
  @Input() surveySelectable: boolean = false;
  @Input() filterable: boolean = true;
  @Input() groupedByHistory = true;
  @Input() inputColumns = null;
  @Input() outerTableRowIsNavigable = false;
  @Output() surveySelected = new EventEmitter();

  constructor(
    public userService: UserService,
    public organizationService: OrganizationService,
    public decisionService: DecisionService,
    public route: ActivatedRoute,
    public router: Router,
    public surveyService: SurveyService,
    public dialog: MatDialog,
    private cd: ChangeDetectorRef
  ) {
  }

  dataSource: MatTableDataSource<Survey>;
  outerColumnsToDisplay = ['title', 'created_at', 'created_by_user', 'visibility_type', 'revisionsNumber', 'action'];
  columnsToDisplay = ['created_at', 'created_by_user', 'session_number', 'action'];
  expandedSessionsElement: Survey | null;

  loading = false;
  surveys = [];

  searchModel: any = {
    startDate: undefined,
    endDate: undefined,
    name: ''
  };
  typologiesList: string[] = [
    'Private',
    'Public'
  ];
  selectedTypologies = new FormControl('');

  ngOnInit(): void {
    if (this.inputColumns) {
      this.columnsToDisplay = this.inputColumns;
    }
    if (this.input_surveys) {
      this.setDataSourceTable(JSON.parse(JSON.stringify(this.input_surveys)));
    } else {
      this.getAllSurveys();
    }
  }

  public getAllSurveys() {
    this.loading = true;
    this.surveyService.getSurveysByProjectId(this.projectId, (result) => {
      this.setDataSourceTable(result);
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  public setDataSourceTable(surveys) {
    this.surveys = surveys;

    const tmp = this.groupedByHistory ? this.groupSurveysByHistory(surveys) : surveys;
    if (!this.groupedByHistory) {
      this.outerColumnsToDisplay = this.columnsToDisplay;
    }

    this.dataSource = new MatTableDataSource<Survey>(tmp);
    setTimeout(() => {
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.sort) {
        this.dataSource.sort = this.sort;
        this.sort.active = 'created_at';
        this.sort.direction = 'desc';
        this.sort.sortChange.emit({ active: 'created_at', direction: 'desc' });
      }
    }, 100);
  }

  toggleRow(element) {
    if (this.outerTableRowIsNavigable) {
      this.goToSurvey(element?.id);
    } else if ((element.revisions?.data?.length || 0) > 1) {
      this.toggleSessionsRow(element, new Event('click'));
    }
  }

  toggleSessionsRow(element: Survey, event: Event) {
    event.stopPropagation();
    this.expandedSessionsElement = this.expandedSessionsElement === element ? null : element;
    this.cd.detectChanges();
    setTimeout(() => {
      this.innerTables.forEach((table, index) => (table.dataSource as MatTableDataSource<Survey>).sort = this.innerSort.toArray()[index]);
    }, 2000);
  }

  applyFilter(filterValue: string) {
    this.innerTables.forEach((table, index) => (table.dataSource as MatTableDataSource<Survey>).filter = filterValue.trim().toLowerCase());
  }

  groupSurveysByHistory(surveys: Survey[]) {
    const grouped: { [key: number]: Survey[] } = {};

    for (const survey of surveys) {
      if (!grouped[survey.survey_history_id]) {
        grouped[survey.survey_history_id] = [];
      }
      grouped[survey.survey_history_id].push(survey);
    }

    const result = Object.values(grouped)
      .map((group) => {
        const base = group.find((s) => s.session_number === 1);
        if (!base) { return null; }

        const maxMajorVersion = Math.max(...group.map(s => s.major_version));

        return {
          ...base,
          revisions: new MatTableDataSource(group),
          versionsNumber: maxMajorVersion
        };
      })
      .filter((s): s is Survey & {
        revisions: MatTableDataSource<Survey>;
        versionsNumber: number;
      } => s !== null);

    return result;
  }

  public getCreatorFullNameBySurvey(survey) {
    const userDetails = survey.created_by_user.details;

    if (userDetails.first_name && userDetails.last_name) {
      return userDetails.first_name + ' ' + userDetails.last_name;
    } else {
      return survey.created_by_user.email;
    }
  }

  capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.toLowerCase().slice(1);
  }

  applyFilterToDataSource() {
    this.dataSource.data = [...this.surveys];
    if (this.searchModel.name.length > 0) {
      this.dataSource.data = this.dataSource.filteredData.filter((item) => {
        // @ts-ignore
        return item.title
          .toLowerCase()
          .includes(this.searchModel.name.toLowerCase());
      });
    }
    if (this.searchModel.startDate) {
      const startDateObj = new Date(this.searchModel.startDate);

      this.dataSource.data = this.dataSource.filteredData.filter((item) => {
        // @ts-ignore
        const createdAt = new Date(item.created_at);
        return createdAt >= startDateObj;
      });
    }

    if (this.searchModel.endDate) {
      const endDateObj = new Date(this.searchModel.endDate);
      this.dataSource.data = this.dataSource.filteredData.filter((item) => {
        // @ts-ignore
        const closedAt = new Date(item?.closed_at);
        return closedAt <= endDateObj;
      });
    }

    if (this.selectedTypologies.value.length > 0) {
      this.dataSource.data = this.dataSource.filteredData.filter(
        (item) => {
          return this.selectedTypologies.value.includes(this.capitalizeFirstLetter(item?.visibility_type));
        }
      );
    }

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onClickResetFilters() {

    this.searchModel = {
      startDate: undefined,
      endDate: undefined,
      name: ''
    };

    this.selectedTypologies = new FormControl('');

    this.dataSource.data = [...this.surveys];
  }

  addNewSurvey() {
    const dialogRef = this.dialog.open(ModalSurveyDetailsComponent, {
      panelClass: 'fullscreen-dialog',
      disableClose: true,
      data: {
        projectId: this.projectId,
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== null && result !== undefined && result?.id) {
        this.router.navigate(['surveys/' + result?.id]);
      }
    });
  }

  goToSurvey(id) {
    this.router.navigate(['/']).then(() => {
      this.router.navigate(['/surveys/' + id]);
    });
  }
}

export interface Survey {
  title: string;
  created_at: Date;
  created_by_user: number;
  visibility_type: string;
  action: string;
  revisions: MatTableDataSource<Survey>;
  session_number: number;
  survey_history_id: number;
  versionsNumber: number;
  major_version: number;
}
